import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Records', href: '/records' },
    {
      name: 'Books',
      href: '/books',
      dropdown: [
        { name: 'Published Books', href: '/books/published' },
        { name: 'Upcoming Book Chapter', href: '/books/upcoming' },
        { name: 'Publish a Book', href: '/books/publish' }
      ]
    },
    {
      name: 'Conference',
      href: '/conference',
      dropdown: [
        { name: 'Past Conference', href: '/conference/past' },
        { name: 'Upcoming Conference', href: '/conference/upcoming' }
      ]
    },
    { name: 'Awards', href: '/awards' },
    { name: 'Events', href: '/events' },
    { name: 'Authors', href: '/authors' },
    { name: 'Legends', href: '/legends' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <>
      <style>{`
        .header-root {
          position: sticky;
          top: 0;
          z-index: 99999;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid #e5e7eb;
          box-shadow: 0 1px 8px rgba(0,0,0,0.07);
        }
        .header-inner {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 72px;
        }
        .desktop-nav { display: none; }
        .nav-list {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .nav-item { position: relative; }
        .nav-link, .nav-trigger {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 8px 12px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          text-decoration: none;
          border-radius: 8px;
          border: none;
          background: none;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .nav-link:hover, .nav-trigger:hover {
          background: #f3f4f6;
          color: #2563eb;
        }
        .nav-link.active, .nav-trigger.active {
          color: #2563eb;
          background: #eff6ff;
          font-weight: 600;
        }
        .nav-chevron {
          transition: transform 0.2s;
        }
        .nav-chevron.open { transform: rotate(180deg); }
        .dropdown-menu {
          position: fixed;
          top: 72px;
          width: 210px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 12px 32px rgba(0,0,0,0.13);
          z-index: 999999;
          overflow: hidden;
          padding: 6px;
        }
        .dropdown-item {
          display: block;
          padding: 10px 14px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          text-decoration: none;
          border-radius: 8px;
          transition: background 0.15s, color 0.15s;
        }
        .dropdown-item:hover {
          background: #eff6ff;
          color: #2563eb;
        }
        .mobile-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          color: #374151;
        }
        .mobile-btn:hover { background: #f3f4f6; }
        .mobile-nav {
          border-top: 1px solid #e5e7eb;
          background: #fff;
          padding: 12px 16px 16px;
        }
        .mobile-nav-link, .mobile-nav-trigger {
          display: block;
          padding: 10px 12px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #374151;
          text-decoration: none;
          border-radius: 8px;
          border: none;
          background: none;
          cursor: pointer;
          width: 100%;
          text-align: left;
          transition: background 0.15s;
        }
        .mobile-nav-link:hover, .mobile-nav-trigger:hover { background: #f3f4f6; }
        .mobile-sub-link {
          display: block;
          padding: 9px 12px 9px 28px;
          font-size: 0.85rem;
          color: #4b5563;
          text-decoration: none;
          border-radius: 8px;
          transition: background 0.15s;
        }
        .mobile-sub-link:hover { background: #f3f4f6; color: #2563eb; }
        @media(min-width: 768px) {
          .desktop-nav { display: block; }
          .mobile-btn { display: none; }
          .mobile-nav { display: none; }
        }
      `}</style>

      <header className="header-root">
        <div className="header-inner">
          <Logo />

          {/* Desktop Nav */}
          <nav className="desktop-nav">
            <ul className="nav-list">
              {navigation.map((item) => (
                <li key={item.name} className="nav-item"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                  onMouseLeave={() => item.dropdown && setActiveDropdown(null)}
                >
                  {!item.dropdown ? (
                    <Link
                      to={item.href}
                      className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <>
                      <button className={`nav-trigger ${isActive(item.href) ? 'active' : ''}`}>
                        {item.name}
                        <ChevronDown size={14} className={`nav-chevron ${activeDropdown === item.name ? 'open' : ''}`} />
                      </button>
                      {activeDropdown === item.name && (
                        <DropdownPortal itemName={item.name}>
                          {item.dropdown.map((sub) => (
                            <Link
                              key={sub.name}
                              to={sub.href}
                              className="dropdown-item"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </DropdownPortal>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile button */}
          <button className="mobile-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="mobile-nav">
            {navigation.map((item) => (
              <div key={item.name}>
                {!item.dropdown ? (
                  <Link to={item.href} className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                    {item.name}
                  </Link>
                ) : (
                  <>
                    <button
                      className="mobile-nav-trigger"
                      onClick={() => setMobileDropdown(mobileDropdown === item.name ? null : item.name)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      {item.name}
                      <ChevronDown size={14} style={{ transform: mobileDropdown === item.name ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                    </button>
                    {mobileDropdown === item.name && item.dropdown.map((sub) => (
                      <Link key={sub.name} to={sub.href} className="mobile-sub-link" onClick={() => setIsMenuOpen(false)}>
                        {sub.name}
                      </Link>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </header>
    </>
  );
};

// Renders dropdown at a fixed position calculated from the trigger element
const DropdownPortal = ({ itemName, children }) => {
  const ref = React.useRef(null);
  const [pos, setPos] = React.useState({ left: 0 });

  React.useEffect(() => {
    // Find the trigger button for this nav item
    const triggers = document.querySelectorAll('.nav-trigger');
    for (const t of triggers) {
      if (t.textContent.trim().startsWith(itemName)) {
        const rect = t.getBoundingClientRect();
        setPos({ left: rect.left });
        break;
      }
    }
  }, [itemName]);

  return (
    <div ref={ref} className="dropdown-menu" style={{ left: pos.left }}>
      {children}
    </div>
  );
};

export default Header;
